import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { MAIN_URL } from "../../../Configurations/Urls";
import {
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const SOURCE_TOOLBAR_FILTERS = [
  {
    urlKey: "source_type",
    field: "organization_recruitment_source_type_id",
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_recruitment_source_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          // Keep numeric IDs as-is; only lowercase string labels for case-insensitive match
          filterValue = filterValue.split(",").map((v) => {
            const trimmed = v.trim();
            return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
          });
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "recruitment_source_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const normalizeAppliedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((f) => f?.field && f?.operator)
    .map((f) => {
      const requiresValue = operatorRequiresValue(f.operator);
      if (!requiresValue) {
        return { field: f.field, operator: f.operator, value: null };
      }

      let value = f.value;
      if (value === null || value === undefined || value === "") return null;
      if (Array.isArray(value) && value.length === 0) return null;

      if (["equals", "not_equals"].includes(f.operator) && typeof value === "string") {
        value = value.split(",").map((v) => {
          const trimmed = v.trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      } else if (
        ["equals", "not_equals"].includes(f.operator) &&
        Array.isArray(value)
      ) {
        value = value.map((v) => {
          const trimmed = String(v).trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      }

      return { field: f.field, operator: f.operator, value };
    })
    .filter(Boolean);
};

const DEFAULT_COLUMNS = [
  {
    field: "recruitment_source_name",
    label: "Source Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "recruitment_source_type_name",
    label: "Source Type",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "primary_contact",
    label: "Primary Contact",
    visible: true,
    width: 200,
    filterable: false,
    sortable: false,
    pinned: "none",
    required: false,
  },
  {
    field: "website_url",
    label: "Website",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "previous_recruitment_count",
    label: "Previous Count",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_active",
    label: "Is Active",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function RecruitmentSourcesList() {
  const [rows, setRows] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [sourceTypes, setSourceTypes] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_recruitment_sources_grid_${org.organization_id}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (serverCfg?.columns?.length > 0) {
              const cols = [...serverCfg.columns];
              const hasPrimary = cols.some(
                (c) =>
                  c.field === "primary_contact" ||
                  c.key === "primary_contact"
              );
              if (!hasPrimary) {
                const typeIdx = cols.findIndex(
                  (c) =>
                    c.field === "recruitment_source_type_name" ||
                    c.key === "recruitment_source_type_name"
                );
                const primaryCol = DEFAULT_COLUMNS.find(
                  (c) => c.field === "primary_contact"
                );
                if (primaryCol) {
                  cols.splice(typeIdx >= 0 ? typeIdx + 1 : 1, 0, {
                    ...primaryCol,
                    key: "primary_contact",
                  });
                }
              }
              setConfigColumns(cols);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetch(
      `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-type?active_only=1`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        if (cancelled) return;
        setSourceTypes(asOptionList(data, ["recruitment_source_types"]));
      })
      .catch((error) => {
        console.error("Failed to load recruitment source types", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          appliedFilters !== null
            ? normalizeAppliedFilters(appliedFilters)
            : buildFiltersFromParams(searchParams),
          searchParams,
          SOURCE_TOOLBAR_FILTERS
        );
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field:
              searchParams.get("sortBy") ||
              "organization_recruitment_source_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-sources-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setRows(
          data.map((item) => {
            const name = item?.primary_contact_name || "";
            const phone = item?.primary_contact_phone || "";
            const primaryContact =
              name || phone ? (
                <Box sx={{ lineHeight: 1.25, py: 0.25 }}>
                  <Typography
                    component="div"
                    sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
                  >
                    {name || "—"}
                  </Typography>
                  {phone ? (
                    <Typography
                      component="div"
                      sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
                    >
                      {phone}
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                "—"
              );

            return {
              ...item,
              id: item?.organization_recruitment_source_id,
              is_active:
                item?.is_active == 1 || item?.is_active === true ? "✔" : "✖",
              primary_contact: primaryContact,
            };
          })
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching recruitment sources:", error);
        toast.error("Failed to load recruitment sources");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams, appliedFilters]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Recruitment source deleted");
          setAppliedFilters((prev) => (prev === null ? [] : [...prev]));
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            toast.error(
              errorData.message ||
                errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
                "Failed to delete recruitment source"
            );
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete recruitment source");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/recruitment/sources/edit/${item.id || item.organization_recruitment_source_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/recruitment/sources/view/${item.id || item.organization_recruitment_source_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback((nextFilters) => {
    // Use filters from the table directly — do NOT clone URLSearchParams here.
    setAppliedFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Recruitment Sources"}
        btnName={"Add Recruitment Source"}
        delete_action={"RECRUITMENT_SOURCES_DELETE"}
        add_action={"RECRUITMENT_SOURCES_ADD"}
        Data={rows}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "recruitment sources",
          "recruitment sources",
          "Add Recruitment Source",
          "recruitment source",
        ]}
        Route={"/recruitment/sources"}
        setData={setRows}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_recruitment_sources"
          primaryKey="organization_recruitment_source_id"
          heading="Recruitment Sources"
          data={rows}
          sortname="organization_recruitment_source_id"
          showActions={true}
          Route="/recruitment/sources"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={[
            "RECRUITMENT_SOURCES_DELETE",
            "RECRUITMENT_SOURCES_EDIT",
          ]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_recruitment_sources"
          toolbarExtras={
            <ToolbarFilterSelect
              label="Source Type"
              urlKey="source_type"
              allLabel="All source types"
              options={sourceTypes}
              valueKey="organization_recruitment_source_type_id"
              labelKey="recruitment_source_type_name"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
            />
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default RecruitmentSourcesList;
